import React, { useState } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, BookOpen, TrendingUp, FileText, Shield, CreditCard, Menu, X, Settings, LogOut } from 'lucide-react';

const EducationCenter = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const articles = [
    {
      category: 'Credit Basics',
      icon: <BookOpen className="w-6 h-6" />,
      title: 'Understanding Your Credit Score',
      description: 'Learn how credit scores are calculated and what factors impact them most',
      readTime: '5 min read'
    },
    {
      category: 'Dispute Process',
      icon: <FileText className="w-6 h-6" />,
      title: 'How to Write Effective Dispute Letters',
      description: 'Master the art of credit dispute letters with our comprehensive guide',
      readTime: '8 min read'
    },
    {
      category: 'Strategy',
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'The 3-Bureau Attack Strategy',
      description: 'Learn how to maximize results by disputing with all three bureaus simultaneously',
      readTime: '6 min read'
    },
    {
      category: 'Legal Rights',
      icon: <Shield className="w-6 h-6" />,
      title: 'Your Rights Under FCRA',
      description: 'Understand your legal protections and how to use them effectively',
      readTime: '10 min read'
    },
    {
      category: 'Credit Building',
      icon: <CreditCard className="w-6 h-6" />,
      title: 'Building Credit After Disputes',
      description: 'Best practices for maintaining and improving your credit score',
      readTime: '7 min read'
    },
    {
      category: 'Advanced',
      icon: <Sparkles className="w-6 h-6" />,
      title: 'Metro 2 Compliance & Data Furnishers',
      description: 'Deep dive into how credit reporting actually works',
      readTime: '12 min read'
    }
  ];

  const faqs = [
    {
      question: 'How long does the dispute process take?',
      answer: 'Credit bureaus have 30 days to investigate disputes by law. Most results come within 30-45 days.'
    },
    {
      question: 'Can I dispute multiple items at once?',
      answer: 'Yes! Our AI can generate multiple dispute letters simultaneously for maximum efficiency.'
    },
    {
      question: 'What if my dispute is denied?',
      answer: "Don't give up! You can re-dispute with additional evidence or try a different approach."
    },
    {
      question: 'Will disputing hurt my credit score?',
      answer: 'No, the dispute process itself does not hurt your score. Only the resolution can change it.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/dashboard')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Dashboard</button>
              <button onClick={() => navigate('/disputes')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Disputes</button>
              <button onClick={() => navigate('/education')} className="text-violet-600 font-medium">Learn</button>
              <button onClick={() => navigate('/settings')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="font-heading">
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <button onClick={() => navigate('/dashboard')} className="block w-full text-left text-slate-600 font-medium">Dashboard</button>
              <button onClick={() => navigate('/disputes')} className="block w-full text-left text-slate-600 font-medium">Disputes</button>
              <button onClick={() => navigate('/education')} className="block w-full text-left text-violet-600 font-medium">Learn</button>
              <button onClick={() => navigate('/settings')} className="block w-full text-left text-slate-600 font-medium">Settings</button>
              <Button onClick={() => { logout(); navigate('/'); }} variant="ghost" className="w-full justify-start">Logout</Button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-2">Education Center</h1>
          <p className="text-slate-600">Learn everything about credit repair and financial health</p>
        </div>

        {/* Articles Grid */}
        <div className="mb-12">
          <h2 className="text-2xl font-heading font-bold mb-6">Featured Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article, index) => (
              <div 
                key={index}
                className="bg-white rounded-3xl p-6 border-2 border-slate-100 hover:border-violet-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-violet-100 text-violet-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    {article.icon}
                  </div>
                  <span className="text-sm font-medium text-violet-600">{article.category}</span>
                </div>
                <h3 className="text-lg font-heading font-semibold mb-2">{article.title}</h3>
                <p className="text-slate-600 text-sm mb-4">{article.description}</p>
                <div className="text-xs text-slate-500">{article.readTime}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div>
          <h2 className="text-2xl font-heading font-bold mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-3xl p-6 border-2 border-slate-100">
                <h3 className="text-lg font-heading font-semibold mb-2">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-3xl p-8 text-center text-white">
          <h3 className="text-2xl font-heading font-bold mb-4">Ready to Start Fixing Your Credit?</h3>
          <p className="mb-6">Put your knowledge into action with AI-powered dispute letters</p>
          <Button
            onClick={() => navigate('/disputes')}
            className="bg-white text-violet-600 hover:bg-slate-100 rounded-full px-8 py-6 font-heading font-semibold hover:scale-105 transition-all"
          >
            Create Your First Dispute
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EducationCenter;